/**
 * @fileoverview TimesheetChatSheet — wires the generic `ChatSheet` to the
 * timesheet chat backend (MOR-906).
 *
 * Mirrors `ContactChatSheet` mechanics exactly (same shared chat UI, same
 * streaming/optimistic/thinking logic) but threads on `(orgId, userId)` and
 * sends through `api.timesheets.chat.sendTimesheetChatMessage`. The agent
 * can read the user's entries, log DRAFT entries, and answer reporting
 * questions. Opened from the quick-entry bar's expand button — the typed
 * text carries over via `initialInput`.
 *
 * Used by:
 * - src/components/timesheets/WeeklyTimesheet.tsx
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { useThreadMessages } from "@convex-dev/agent/react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatSheet,
  type ChatMentionItem,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";

interface TimesheetChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  /** Text carried over from the quick-entry bar, pre-filled in the composer. */
  initialInput?: string;
}

export function TimesheetChatSheet({
  open,
  onOpenChange,
  orgId,
  initialInput,
}: TimesheetChatSheetProps) {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Reset thread state when the org switches.
  useEffect(() => {
    setActiveThreadId(null);
    setIsNewThread(false);
    setPendingMessage(null);
    setWaitingForResponse(false);
  }, [orgId]);

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: (currentUser as { avatarUrl?: string }).avatarUrl,
      firstName: (currentUser as { firstName?: string }).firstName,
      lastName: (currentUser as { lastName?: string }).lastName,
      email: (currentUser as { email?: string }).email,
    };
  }, [currentUser]);

  const { data: threadList } = useQuery(
    convexQuery(api.timesheets.chatInternal.listTimesheetChatThreads, {
      orgId,
    }),
  );

  // `@`-mention items: matters, categories and staff — the agent resolves
  // the inserted names via its context tool, so mentions are a fast way to
  // reference things by their exact names.
  const { data: mentionContext } = useQuery(
    convexQuery(api.timesheets.entries.entryContext, { orgId }),
  );
  const { data: mentionStaff } = useQuery(
    convexQuery(api.timesheets.profiles.listOrgStaff, { orgId }),
  );
  const mentionItems = useMemo<ChatMentionItem[]>(() => {
    const jobs = (mentionContext?.jobs ?? []).map((j) => ({
      id: j._id as string,
      name: j.name,
      subtitle: j.jobCode ? `Matter · ${j.jobCode}` : "Matter",
    }));
    const categories = (mentionContext?.categories ?? []).map((c) => ({
      id: c._id as string,
      name: c.name,
      subtitle: "Category",
    }));
    const staff = (mentionStaff ?? []).map((m) => ({
      id: m.userId as string,
      name: m.name,
      subtitle: "Staff",
    }));
    return [...jobs, ...categories, ...staff];
  }, [mentionContext, mentionStaff]);

  useEffect(() => {
    if (
      !activeThreadId &&
      !isNewThread &&
      threadList &&
      threadList.length > 0
    ) {
      setActiveThreadId(threadList[0].threadId);
    }
  }, [activeThreadId, isNewThread, threadList]);

  const threadMessages = useThreadMessages(
    api.timesheets.chatInternal.listTimesheetChatMessages,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  const realMessages: ChatMessageData[] = (threadMessages?.results ?? [])
    .filter((m) => {
      if (m.message?.role !== "user" && m.message?.role !== "assistant")
        return false;
      const content = m.message?.content;
      if (!content) return false;
      if (typeof content === "string") return content.trim().length > 0;
      if (Array.isArray(content)) {
        return content.some(
          (part) => part.type === "text" && part.text?.trim(),
        );
      }
      return false;
    })
    .map((m) => {
      const content = m.message?.content ?? "";
      const text =
        typeof content === "string"
          ? content
          : content
              .filter((p) => p.type === "text")
              .map((p) => p.text ?? "")
              .join("");

      return {
        id: m.key ?? m._id ?? String(m._creationTime),
        role: (m.message?.role ?? "assistant") as "user" | "assistant",
        content: text,
        createdAt: m._creationTime,
        isStreaming: m.streaming === true,
      };
    });

  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = threadMessages?.results ?? [];
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }

    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      if (m.message?.role === "assistant" && Array.isArray(m.message.content)) {
        for (const part of m.message.content) {
          const tc = part as { type?: string; toolName?: string };
          if (tc.type === "tool-call" && tc.toolName) {
            tools.push(tc.toolName);
          }
        }
      }
    }

    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;

    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }

    const content = lastRaw.message?.content;
    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(content) &&
      content.some((p) => p.type === "tool-call");

    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;

    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) {
      setWaitingForResponse(false);
    }
  }, [waitingForResponse, isAgentThinking]);

  const { mutateAsync: sendAction } = useMutation({
    mutationFn: useConvexAction(api.timesheets.chat.sendTimesheetChatMessage),
  });

  const handleSend = useCallback(
    async (message: string) => {
      setPendingMessage(message);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        const result = await sendAction({
          orgId,
          message,
          threadId: activeThreadId ?? undefined,
          // Client-local date so "today"/"yesterday" resolve in the user's
          // timezone, not server UTC.
          clientDate: format(new Date(), "yyyy-MM-dd"),
        });
        if (!activeThreadId) {
          setActiveThreadId(result.threadId);
          setIsNewThread(false);
        }
      } catch (err) {
        const friendly =
          err instanceof Error
            ? err.message
            : "Could not send your message — please try again.";
        console.error("[TimesheetChatSheet] Failed to send message:", err);
        setPendingMessage(null);
        setWaitingForResponse(false);
        toast.error(friendly);
      } finally {
        setTimeout(() => setIsSending(false), 500);
      }
    },
    [sendAction, orgId, activeThreadId],
  );

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsNewThread(true);
  }, []);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setIsNewThread(false);
  }, []);

  const threads = useMemo(
    () =>
      (threadList ?? []).map((t) => ({
        threadId: t.threadId,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        preview: t.preview,
      })),
    [threadList],
  );

  const emptyMessage =
    "Log and review your time in plain English.\n\n" +
    "Try:\n" +
    "• “Log 3 units reviewing the POD from Daimler on Vale Tudo, creditors.”\n" +
    "• “What have I logged this week?”\n" +
    "• “Yesterday: 2 units creditor calls on Acme, 5 units drafting the s439A report.”";

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Timesheet assistant"
      description="Log time, fill gaps and answer questions about your recorded time. Everything it logs is a draft for your review."
      messages={messages}
      onSend={handleSend}
      userInfo={userInfo}
      isSending={isSending}
      isThinking={isAgentThinking}
      activeToolCalls={activeToolCalls}
      emptyMessage={emptyMessage}
      inputPlaceholder="Log time or ask about your timesheet — @ mentions matters, staff and categories…"
      initialInput={initialInput}
      mentionItems={mentionItems}
      threads={threads}
      activeThreadId={activeThreadId}
      onNewThread={handleNewThread}
      onSelectThread={handleSelectThread}
    />
  );
}
